import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useCustomerDetailQuery, useUpdateCustomerMutation } from '../../query/hooks';
import { CustomerFormAddressContactTabScreen } from './TabScreens/CustomerFormAddressContact';
import { CustomerFormCommercialNotesTabScreen } from './TabScreens/CustomerFormCommercialNotes';
import { CustomerFormIdentityTabScreen } from './TabScreens/CustomerFormIdentity';
import { CustomerFormOfficeWorkTabScreen } from './TabScreens/CustomerFormOfficeWork';
import {
  customerFormIssues,
  customerFormTabLabel,
  customerRequiredFieldKeySet,
  type CustomerFormTabKey,
} from './customerCompleteness';
import type { CustomerFormDraft } from './customerFormDraft';
import {
  customerFormDraftFromRow,
  draftToCustomerPayload,
  isPlaceholderCustomerNumber,
} from './customerFormDraft';

export type CustomerFormDialogCustomer = {
  documentId: string;
  name: string;
};

type Props = {
  open: boolean;
  documentId: string | null;
  /** Lebih dari satu: panel kiri. Kosongkan untuk satu pelanggan. */
  customers?: CustomerFormDialogCustomer[];
  /** Pelanggan yang belum lolos simpan di sesi ini. */
  pendingIds?: string[];
  jwt: string | null;
  onClose: () => void;
  onSaved: (savedDocumentId: string) => void;
};

/** Form ubah pelanggan di modal. NPWP wajib, ditambah field wajib form buat baru. */
export function CustomerFormDialog({
  open,
  documentId,
  customers,
  pendingIds = [],
  jwt,
  onClose,
  onSaved,
}: Props) {
  const customerList = customers ?? [];
  const showList = customerList.length > 1;
  const [activeId, setActiveId] = useState<string | null>(documentId);
  const detailQ = useCustomerDetailQuery(jwt, open ? activeId : null);
  const updateMut = useUpdateCustomerMutation(jwt);
  const requiredKeys = useMemo(() => customerRequiredFieldKeySet('approve'), []);

  const [drafts, setDrafts] = useState<Record<string, CustomerFormDraft>>({});
  const editorScrollRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<CustomerFormTabKey>('identity');
  const [showValidation, setShowValidation] = useState(false);

  const storedDraft = activeId ? drafts[activeId] : undefined;
  const serverRow = detailQ.data?.documentId === activeId ? detailQ.data : undefined;
  const draft = storedDraft ?? (serverRow ? customerFormDraftFromRow(serverRow) : undefined);
  const draftReady = Boolean(activeId && draft);

  useEffect(() => {
    editorScrollRef.current?.scrollTo({ top: 0 });
  }, [activeId]);

  useEffect(() => {
    if (!open || !activeId || !detailQ.data) return;
    if (detailQ.data.documentId !== activeId) return;
    const row = detailQ.data;
    setDrafts((prev) => {
      if (prev[activeId]) return prev;
      return { ...prev, [activeId]: customerFormDraftFromRow(row) };
    });
  }, [open, activeId, detailQ.data]);

  const formIssues = useMemo(
    () => (showValidation && draft ? customerFormIssues(draft, { mode: 'approve' }) : []),
    [showValidation, draft],
  );
  const fieldErrors = useMemo(() => {
    const map: Partial<Record<keyof CustomerFormDraft, string>> = {};
    for (const issue of formIssues) map[issue.key] = issue.message;
    return map;
  }, [formIssues]);

  function selectCustomer(nextId: string) {
    if (!nextId || nextId === activeId || updateMut.isPending) return;
    updateMut.reset();
    setShowValidation(false);
    setTab('identity');
    setActiveId(nextId);
  }

  async function onSave() {
    const savingId = activeId;
    const savingDraft = savingId ? drafts[savingId] : undefined;
    if (!jwt || !savingId || !savingDraft) return;
    const issues = customerFormIssues(savingDraft, { mode: 'approve' });
    if (issues.length > 0) {
      setShowValidation(true);
      setTab(issues[0].tab);
      return;
    }
    try {
      const payload = draftToCustomerPayload(savingDraft);
      if (isPlaceholderCustomerNumber(savingDraft.customer_number)) payload.customer_number = '';
      const saved = await updateMut.mutateAsync({ documentId: savingId, data: payload });
      const assigned = saved.customer_number?.trim() ?? '';
      if (assigned && assigned !== savingDraft.customer_number.trim()) {
        setDrafts((prev) => {
          const current = prev[savingId];
          if (!current) return prev;
          return { ...prev, [savingId]: { ...current, customer_number: assigned } };
        });
      }
      onSaved(savingId);
    } catch {
      /* surfaced below */
    }
  }

  const activeCustomer = customerList.find((customer) => customer.documentId === activeId);
  const customerName = activeCustomer?.name?.trim() || detailQ.data?.name?.trim();
  const loadingActive = Boolean(activeId) && !draftReady && !detailQ.isError;
  const loadError = !draftReady && detailQ.isError ? detailQ.error : null;

  const editor = (
    <>
      {loadingActive ? (
        <Box
          sx={{
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid="customer-npwp-dialog-loading"
        >
          <CircularProgress />
        </Box>
      ) : null}
      {loadError ? (
        <Alert severity="error">
          {loadError instanceof Error ? loadError.message : 'Gagal memuat pelanggan.'}
        </Alert>
      ) : null}
      {updateMut.isError ? (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
          {updateMut.error instanceof Error ? updateMut.error.message : 'Gagal menyimpan.'}
        </Alert>
      ) : null}
      {formIssues.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Lengkapi field yang wajib: {formIssues.map((issue) => issue.label).join(', ')}.
        </Alert>
      ) : null}
      {draft ? (
        <>
          {showList && customerName ? (
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {customerName}
            </Typography>
          ) : null}
          <Tabs value={tab} onChange={(_, v: CustomerFormTabKey) => setTab(v)}>
            <Tab label={customerFormTabLabel('Identitas', 'identity', formIssues)} value="identity" />
            <Tab label={customerFormTabLabel('Alamat & kontak', 'address', formIssues)} value="address" />
            <Tab label={customerFormTabLabel('Kantor & pekerjaan', 'office', formIssues)} value="office" />
            <Tab
              label={customerFormTabLabel('Komersial & catatan', 'commercial', formIssues)}
              value="commercial"
            />
          </Tabs>
          <Box sx={{ py: 2 }}>
            {tab === 'identity' ? (
              <CustomerFormIdentityTabScreen
                draft={draft}
                onChange={(patch) =>
                  setDrafts((prev) => ({
                    ...prev,
                    [activeId!]: { ...(prev[activeId!] ?? draft), ...patch },
                  }))
                }
                fieldErrors={fieldErrors}
                requiredKeys={requiredKeys}
                npwpFormatHint
              />
            ) : null}
            {tab === 'address' ? (
              <CustomerFormAddressContactTabScreen
                draft={draft}
                onChange={(patch) =>
                  setDrafts((prev) => ({
                    ...prev,
                    [activeId!]: { ...(prev[activeId!] ?? draft), ...patch },
                  }))
                }
                fieldErrors={fieldErrors}
                requiredKeys={requiredKeys}
              />
            ) : null}
            {tab === 'office' ? (
              <CustomerFormOfficeWorkTabScreen
                draft={draft}
                onChange={(patch) =>
                  setDrafts((prev) => ({
                    ...prev,
                    [activeId!]: { ...(prev[activeId!] ?? draft), ...patch },
                  }))
                }
                fieldErrors={fieldErrors}
                requiredKeys={requiredKeys}
              />
            ) : null}
            {tab === 'commercial' ? (
              <CustomerFormCommercialNotesTabScreen
                draft={draft}
                onChange={(patch) =>
                  setDrafts((prev) => ({
                    ...prev,
                    [activeId!]: { ...(prev[activeId!] ?? draft), ...patch },
                  }))
                }
                fieldErrors={fieldErrors}
                requiredKeys={requiredKeys}
              />
            ) : null}
          </Box>
        </>
      ) : null}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (updateMut.isPending) return;
        onClose();
      }}
      maxWidth={showList ? 'lg' : 'md'}
      fullWidth
      sx={
        showList
          ? { '& .MuiDialog-paper': { height: 'min(840px, calc(100% - 64px))' } }
          : undefined
      }
      data-testid="customer-npwp-dialog"
    >
      <DialogTitle>
        {showList || !customerName ? 'Lengkapi data pelanggan' : `Lengkapi data pelanggan — ${customerName}`}
      </DialogTitle>
      <DialogContent
        sx={showList ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' } : undefined}
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          {showList
            ? 'Pilih pelanggan di kiri. Simpan menyimpan pelanggan yang sedang aktif. NPWP wajib diisi sebelum pelanggan setuju.'
            : 'NPWP wajib diisi sebelum pelanggan setuju. Nama, jenis pelanggan, nama kontak, alamat, dan alamat LHU juga wajib bila masih kosong.'}
        </Alert>
        {showList ? (
          <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0, alignItems: 'stretch' }}>
            <Box
              sx={{
                width: 260,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                borderRight: 1,
                borderColor: 'divider',
                pr: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ px: 1, pb: 1 }}>
                Pelanggan
              </Typography>
              <List dense disablePadding sx={{ flex: 1, overflow: 'auto' }}>
                {customerList.map((customer) => {
                  const pending = pendingIds.includes(customer.documentId);
                  return (
                    <ListItemButton
                      key={customer.documentId}
                      selected={customer.documentId === activeId}
                      disabled={updateMut.isPending}
                      onClick={() => selectCustomer(customer.documentId)}
                      data-testid={`customer-npwp-dialog-customer-${customer.documentId}`}
                    >
                      <ListItemText
                        primary={customer.name}
                        secondary={pending ? 'Data belum lengkap' : 'Data lengkap'}
                        primaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
            <Box ref={editorScrollRef} sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto' }}>
              {editor}
            </Box>
          </Box>
        ) : (
          editor
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={updateMut.isPending}>
          Batal
        </Button>
        <Button
          variant="contained"
          onClick={() => void onSave()}
          disabled={updateMut.isPending || !draftReady || !activeId}
          data-testid="customer-npwp-dialog-save"
        >
          {updateMut.isPending ? 'Menyimpan…' : 'Simpan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
